import { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import styled, { keyframes } from "styled-components";
import { publicRequest } from "../requestMethods";
import theme from "../theme";
import { IconBell, IconCheck } from "../icons";
import { playTing } from "../tingSound";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ── Bell wrapper ── */
const Wrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const BellBtn = styled.button`
  background: transparent;
  border: 1.5px solid ${(p) => (p.$hasUnread ? theme.primary : theme.cardBorder)};
  border-radius: 6px;
  padding: 6px 10px;
  color: ${(p) => (p.$hasUnread ? theme.primary : theme.textMuted)};
  cursor: pointer;
  display: flex;
  align-items: center;
  position: relative;
  transition: color 0.15s, border-color 0.15s, background 0.15s;

  &:hover {
    color: ${theme.primary};
    border-color: ${theme.primary};
    background: ${theme.primaryLight};
  }
`;

const Badge = styled.span`
  position: absolute;
  top: -5px;
  right: -5px;
  background: ${theme.danger};
  color: #fff;
  border-radius: 50%;
  font-size: 9px;
  font-weight: 700;
  min-width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 3px;
  border: 1.5px solid ${theme.cardBg};
  pointer-events: none;
`;

/* ── Dropdown panel ── */
const Panel = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  width: 320px;
  max-height: 420px;
  background: ${theme.cardBg};
  border: 1.5px solid ${theme.cardBorder};
  border-radius: 10px;
  box-shadow: 0 12px 40px rgba(26, 18, 8, 0.14);
  z-index: 300;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${fadeIn} 0.22s var(--ease-out-expo, ease) both;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 16px 11px;
  border-bottom: 1.5px solid ${theme.separator};
  flex-shrink: 0;
`;

const PanelTitle = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${theme.textMain};
`;

const MarkAllBtn = styled.button`
  background: transparent;
  border: none;
  font-size: 11.5px;
  font-weight: 600;
  font-family: inherit;
  color: ${theme.primary};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  &:hover { text-decoration: underline; }
`;

const List = styled.div`
  overflow-y: auto;
  flex: 1;
`;

const Item = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  background: ${(p) => (p.$unread ? theme.primaryLight : "transparent")};
  border-bottom: 1px solid ${theme.separator};
  transition: background 0.15s;
  cursor: default;

  &:last-child { border-bottom: none; }
`;

const Dot = styled.div`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${(p) => (p.$unread ? theme.primary : "transparent")};
  flex-shrink: 0;
  margin-top: 5px;
`;

const ItemBody = styled.div`
  flex: 1;
`;

const ItemText = styled.p`
  font-size: 12.5px;
  color: ${theme.textMain};
  margin: 0 0 2px;
  line-height: 1.45;
`;

const ItemTime = styled.span`
  font-size: 11px;
  color: ${theme.textMuted};
`;

const EmptyMsg = styled.p`
  font-size: 13px;
  color: ${theme.textMuted};
  text-align: center;
  padding: 32px 16px;
`;

const InviteActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

const InviteBtn = styled.button`
  border: 1.5px solid ${(p) => (p.$primary ? theme.primary : theme.cardBorder)};
  background: ${(p) => (p.$primary ? theme.primary : "transparent")};
  color: ${(p) => (p.$primary ? "#fff" : theme.textMuted)};
  border-radius: 6px;
  font-size: 11.5px;
  font-weight: 700;
  font-family: inherit;
  padding: 5px 10px;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

function formatTime(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function notifText(n) {
  const who = n.senderName || "Someone";
  if (n.type === "folder_invite") {
    if (n.message) return n.message;
    if (n.inviteStatus === "pending") {
      return `${who} has invited you to join ${n.folderName || "a folder"} folder as "${n.inviteRole || "viewer"}".`;
    }
    if (n.inviteStatus === "accepted") return `You accepted the collaboration for ${n.folderName || "the folder"}.`;
    if (n.inviteStatus === "declined") return `You declined the collaboration for ${n.folderName || "the folder"}.`;
    return "Folder invitation";
  }
  if (n.type === "like") return `${who} liked your note "${n.noteName || "a note"}"`;
  if (n.type === "comment") return `${who} commented on "${n.noteName || "your note"}"`;
  if (n.type === "follow") return `${who} started following you`;
  if (n.type === "new_note") return `${who} posted a new note "${n.noteName || ""}"`;
  if (n.type === "chat_message") return `${who} sent you a message`;
  return "New notification";
}

export default function NotificationBell() {
  const currentUser = useSelector((s) => s.user.currentUser);
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [decisionLoadingId, setDecisionLoadingId] = useState(null);
  const panelRef = useRef(null);

  const unreadCount = notifs.filter((n) => !n.read).length;

  const fetchNotifs = useCallback(async () => {
    if (!currentUser) return;
    try {
      const res = await publicRequest.get(`/notifications/${currentUser._id}`);
      setNotifs(res.data);
    } catch {
      // silently ignore
    }
  }, [currentUser]);

  // Initial fetch + SSE for real-time updates
  useEffect(() => {
    fetchNotifs();
    if (!currentUser) return;

    const BASE = (process.env.REACT_APP_BASE_URL || "http://localhost:8000/api");
    const es = new EventSource(`${BASE}/notifications/sse/${currentUser._id}`);

    es.onmessage = (e) => {
      try {
        const notif = JSON.parse(e.data);
        setNotifs((prev) => [notif, ...prev]);
        playTing();
      } catch { }
    };

    return () => es.close();
  }, [fetchNotifs, currentUser]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleOpen = () => {
    setOpen((v) => !v);
  };

  const handleMarkAll = async () => {
    try {
      await publicRequest.put(`/notifications/${currentUser._id}/read-all`);
      setNotifs((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch {
      // silently ignore
    }
  };

  const handleInviteDecision = async (notificationId, decision) => {
    setDecisionLoadingId(notificationId);
    try {
      const res = await publicRequest.put(`/notifications/${notificationId}/folder-invite-response`, {
        userId: currentUser._id,
        decision,
      });
      setNotifs((prev) => prev.map((n) => (n._id === notificationId ? res.data : n)));
    } catch {
      // silently ignore
    } finally {
      setDecisionLoadingId(null);
    }
  };

  return (
    <Wrap ref={panelRef}>
      <BellBtn
        onClick={handleOpen}
        $hasUnread={unreadCount > 0}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`}
      >
        <IconBell size="15px" />
        {unreadCount > 0 && <Badge>{unreadCount > 9 ? "9+" : unreadCount}</Badge>}
      </BellBtn>

      {open && (
        <Panel>
          <PanelHeader>
            <PanelTitle>Notifications</PanelTitle>
            {unreadCount > 0 && (
              <MarkAllBtn onClick={handleMarkAll}>
                <IconCheck size="11px" /> Mark all read
              </MarkAllBtn>
            )}
          </PanelHeader>

          <List>
            {notifs.length === 0 ? (
              <EmptyMsg>No notifications yet.</EmptyMsg>
            ) : (
              notifs.map((n) => (
                <Item key={n._id} $unread={!n.read}>
                  <Dot $unread={!n.read} />
                  <ItemBody>
                    <ItemText>{notifText(n)}</ItemText>
                    <ItemTime>{formatTime(n.createdAt)}</ItemTime>
                    {n.type === "folder_invite" && n.inviteStatus === "pending" && (
                      <InviteActions>
                        <InviteBtn
                          $primary
                          onClick={() => handleInviteDecision(n._id, "accept")}
                          disabled={decisionLoadingId === n._id}
                        >
                          Accept
                        </InviteBtn>
                        <InviteBtn
                          onClick={() => handleInviteDecision(n._id, "decline")}
                          disabled={decisionLoadingId === n._id}
                        >
                          Decline
                        </InviteBtn>
                      </InviteActions>
                    )}
                  </ItemBody>
                </Item>
              ))
            )}
          </List>
        </Panel>
      )}
    </Wrap>
  );
}
